import { useEffect } from 'react';
import { X } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// The way out of a dialog.
//
// Several of these panels scroll — the equipment editor on a six-radio airplane
// runs past the fold — and their only Cancel sat at the very bottom. A dialog
// you have to scroll to leave is a dialog that feels stuck, so every one of
// them now carries the same mark in the same corner.
//
// Escape closes too: the hook is here rather than in each caller so the two
// can never drift apart.
// ─────────────────────────────────────────────────────────────────────────────

export function useEscapeClose(onClose: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
}

/**
 * Put this FIRST inside a modal panel, and give that panel `relative`.
 * `sticky` keeps it reachable in a panel that scrolls its own content.
 */
export function ModalClose({ onClose, label = 'Close' }: { onClose: () => void; label?: string }) {
  useEscapeClose(onClose);
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label={label}
      className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/80 text-ink-muted backdrop-blur transition-colors hover:bg-slate-100 hover:text-navy-900"
    >
      <X size={18} />
    </button>
  );
}
